import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { listUsers, updateUser, flagUserName } from "@/lib/api/admin";
import type { AdminUser } from "@/lib/api/types";
import { DataTable, type Column } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PAGE_SIZE } from "@/lib/constants";
import { formatDate } from "@/lib/utils/date";
import { formatInt, formatCents } from "@/lib/utils/number";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X } from "lucide-react";

export function UsersPage() {
  const user = useAdminAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Kept in the URL so back-navigation from the user detail page restores the list state.
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("q") ?? "";
  const promoterFilter = searchParams.get("promoter") ?? "all";
  const orderBy = searchParams.get("orderBy") ?? "created_at";
  const orderDir = (searchParams.get("orderDir") ?? "desc") as "asc" | "desc";
  const offset = Number(searchParams.get("offset") ?? "0");

  const [searchInput, setSearchInput] = useState(search);
  const [flagTarget, setFlagTarget] = useState<AdminUser | null>(null);
  const [flagReason, setFlagReason] = useState("");

  const searchRef = useRef<HTMLInputElement>(null);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (searchInput) {
        next.set("q", searchInput);
      } else {
        next.delete("q");
      }
      next.delete("offset");
      return next;
    });
  }

  function clearSearch() {
    setSearchInput("");
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("q");
      next.delete("offset");
      return next;
    });
  }

  function handlePromoterFilterChange(value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === "all") {
        next.delete("promoter");
      } else {
        next.set("promoter", value);
      }
      next.delete("offset");
      return next;
    });
  }

  function handleSort(key: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (orderBy === key) {
        next.set("orderDir", orderDir === "asc" ? "desc" : "asc");
      } else {
        next.set("orderBy", key);
        next.set("orderDir", "desc");
      }
      next.delete("offset");
      return next;
    });
  }

  function handleOffsetChange(newOffset: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newOffset) {
        next.set("offset", String(newOffset));
      } else {
        next.delete("offset");
      }
      return next;
    });
  }

  const { data, isLoading } = useQuery({
    queryKey: ["users", search, promoterFilter, orderBy, orderDir, offset],
    queryFn: async () =>
      listUsers(user!, {
        search: search || undefined,
        isPromoter: promoterFilter === "all" ? undefined : promoterFilter === "true",
        orderBy,
        orderDir,
        limit: PAGE_SIZE,
        offset,
      }),
    enabled: !!user,
  });

  const promoterMutation = useMutation({
    mutationFn: ({ uid, isPromoter }: { uid: string; isPromoter: boolean }) =>
      updateUser(user!, uid, { is_promoter: isPromoter }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const flagNameMutation = useMutation({
    mutationFn: async () => flagUserName(user!, flagTarget!.user_uid, flagReason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
      setFlagTarget(null);
      setFlagReason("");
      toast.success("User name flagged.");
    },
    onError: (err) => toast.error((err as Error).message),
  });

  const columns: Column<AdminUser>[] = [
    {
      key: "name",
      header: "Name",
      render: (u) => (
        <span className="font-medium cursor-pointer hover:underline">
          {u.first_name} {u.last_name}
        </span>
      ),
    },
    {
      key: "is_promoter",
      header: "Promoter",
      render: (u) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            checked={u.is_promoter}
            onCheckedChange={(checked) =>
              promoterMutation.mutate({ uid: u.user_uid, isPromoter: checked })
            }
            disabled={promoterMutation.isPending && promoterMutation.variables?.uid === u.user_uid}
          />
        </div>
      ),
    },
    {
      key: "is_name_flagged",
      header: "Name Flag",
      render: (u) =>
        u.is_name_flagged ? (
          <Badge
            variant="outline"
            className="border-orange-200 text-orange-700 dark:border-orange-800 dark:text-orange-300"
          >
            Flagged
          </Badge>
        ) : (
          <div onClick={(e) => e.stopPropagation()}>
            <Button variant="outline" size="sm" onClick={() => setFlagTarget(u)}>
              Flag
            </Button>
          </div>
        ),
    },
    {
      key: "points_balance",
      header: "Points Balance",
      sortable: true,
      render: (u) => formatInt(u.points_balance),
    },
    {
      key: "accumulated_points",
      header: "Accumulated",
      sortable: true,
      render: (u) => formatInt(u.accumulated_points),
    },
    {
      key: "point_rewards_redeemed",
      header: "Rewards",
      sortable: true,
      render: (u) => formatInt(u.point_rewards_redeemed),
    },
    {
      key: "points_used",
      header: "Points Used",
      sortable: true,
      render: (u) => formatInt(u.points_used),
    },
    {
      key: "saved_lifetime_cents",
      header: "Saved",
      sortable: true,
      render: (u) => formatCents(u.saved_lifetime_cents),
    },
    {
      key: "created_at",
      header: "Joined",
      sortable: true,
      render: (u) => (
        <span className="text-sm text-muted-foreground">{formatDate(u.created_at)}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">Users</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${formatInt(data.total)} users` : "Manage platform users"}
        </p>
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-2">
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md flex-1">
          <div className="relative flex-1">
            <Input
              ref={searchRef}
              placeholder="Search by name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                onClick={clearSearch}
              >
                <X className="size-4" />
              </button>
            )}
          </div>
          <Button type="submit" variant="outline">
            <Search className="size-4" />
          </Button>
        </form>

        <Select value={promoterFilter} onValueChange={handlePromoterFilterChange}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All users</SelectItem>
            <SelectItem value="true">Promoters only</SelectItem>
            <SelectItem value="false">Non-promoters only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        rowKey={(u) => u.user_uid}
        emptyMessage="No users found."
        orderBy={orderBy}
        orderDir={orderDir}
        onSort={handleSort}
        onRowClick={(u) => void navigate(`/users/${u.user_uid}`)}
      />

      {data && (
        <Pagination
          offset={offset}
          limit={PAGE_SIZE}
          total={data.total}
          onOffsetChange={handleOffsetChange}
        />
      )}

      <ConfirmDialog
        open={flagTarget != null}
        onOpenChange={(open) => {
          if (!open) {
            setFlagTarget(null);
            setFlagReason("");
          }
        }}
        title="Flag name for correction"
        description={
          flagTarget &&
          `Flag "${flagTarget.first_name} ${flagTarget.last_name}"'s name as needing correction? They'll be blocked from using the app until they submit a real name.`
        }
        confirmLabel="Flag Name"
        destructive
        isPending={flagNameMutation.isPending}
        onConfirm={() => flagNameMutation.mutate()}
        textarea={{
          value: flagReason,
          onChange: setFlagReason,
          label: "Reason (optional)",
          placeholder: "e.g. looks like a fake name",
          maxLength: 500,
        }}
      />
    </div>
  );
}
